import { Link } from 'react-router-dom';
import {
  Clock, AlertTriangle, User, Building2, ArrowRight,
} from 'lucide-react';
import {
  STATION_LABELS, currentStation, routingProgress,
  type WorkTicket,
} from '@/types/production';
import { TicketStatusBadge, PriorityBadge } from '@/components/production/TicketBadges';

type Props = {
  ticket: WorkTicket;
  compact?: boolean;
};

export function TicketCard({ ticket, compact }: Props) {
  const station = currentStation(ticket);
  const progress = routingProgress(ticket);
  const isLate = ticket.dueDate
    && new Date(ticket.dueDate).getTime() < Date.now()
    && !['complete', 'cancelled'].includes(ticket.status);

  const hasActiveLabor = ticket.labor?.some((l) => !l.endedAt);

  return (
    <Link
      to={`/production/tickets/${ticket.id}`}
      className={`
        block bg-bg-panel border border-border rounded-sm p-3 transition-all
        hover:border-brass-400/60 hover:bg-bg-hover
        ${isLate ? 'border-l-2 border-l-danger' : ''}
        ${hasActiveLabor ? 'ring-1 ring-brass-400/40' : ''}
      `}
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <div className="font-mono text-2xs text-brass-300">{ticket.number}</div>
          <div className="text-sm text-text truncate mt-0.5">{ticket.description}</div>
        </div>
        <PriorityBadge priority={ticket.priority} />
      </div>

      {/* Quantity + specs */}
      {!compact && (
        <div className="flex items-baseline gap-3 mb-2 text-2xs text-text-dim">
          <span className="tabular text-text">
            {ticket.quantity} {ticket.unit ?? 'ea'}
          </span>
          {ticket.specs?.veneerSpecies && (
            <span>· {ticket.specs.veneerSpecies}</span>
          )}
          {ticket.specs?.dimensions && (
            <span>· {ticket.specs.dimensions}</span>
          )}
        </div>
      )}

      {/* Client / order */}
      <div className="flex items-center gap-1.5 text-2xs text-text-dim mb-2">
        {ticket.clientName && (
          <>
            <Building2 size={10} />
            <span className="truncate">{ticket.clientName}</span>
          </>
        )}
        {ticket.orderNumber && (
          <>
            <span>·</span>
            <span className="font-mono">{ticket.orderNumber}</span>
          </>
        )}
      </div>

      {/* Progress bar */}
      <div className="h-1 bg-bg-subtle rounded-full overflow-hidden mb-2">
        <div
          className={`h-full transition-all ${
            ticket.status === 'complete' ? 'bg-success' :
            ticket.status === 'blocked' ? 'bg-warning' :
            'bg-brass-400'
          }`}
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Footer: station + status + due date */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <TicketStatusBadge status={ticket.status} />
          {station && ticket.status !== 'complete' && (
            <span className="text-2xs text-text-muted truncate">
              <ArrowRight size={9} className="inline" /> {STATION_LABELS[station]}
            </span>
          )}
        </div>

        {ticket.dueDate && (
          <div className={`flex items-center gap-1 text-2xs tabular ${
            isLate ? 'text-danger' : 'text-text-dim'
          }`}>
            {isLate ? <AlertTriangle size={9} /> : <Clock size={9} />}
            {new Date(ticket.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </div>
        )}
      </div>

      {/* Active operator */}
      {hasActiveLabor && (
        <div className="mt-2 pt-2 border-t border-border flex items-center gap-1.5 text-2xs text-brass-300">
          <User size={10} />
          {ticket.labor.filter((l) => !l.endedAt).map((l) => l.operatorName).join(', ')}
          <span className="text-text-dim">· running</span>
        </div>
      )}
    </Link>
  );
}
